import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Workstream } from '../types/workstream';
import ArchiveIcon from './icons/ArchiveIcon';
import UnarchiveIcon from './icons/UnarchiveIcon';

interface WorkstreamCardActionsProps {
  workstream: Workstream;
  onWorkstreamChanged: () => void;
}

function PlayIcon({ size = 16 }: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <polygon points="6,3 20,12 6,21" />
    </svg>
  );
}

function PauseIcon({ size = 16 }: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <rect x="5" y="3" width="5" height="18" rx="1" />
      <rect x="14" y="3" width="5" height="18" rx="1" />
    </svg>
  );
}

function PlanIcon({ size = 16 }: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function MoreIcon({ size = 16 }: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
}

const iconBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  padding: 0,
  background: 'none',
  border: '1px solid rgba(0,0,0,0.15)',
  borderRadius: '4px',
  cursor: 'pointer',
  color: '#24292f',
};

export default function WorkstreamCardActions({ workstream, onWorkstreamChanged }: WorkstreamCardActionsProps) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const wsUrl = `/api/workstreams/${encodeURIComponent(workstream.workstreamID)}`;

  const handleStartPause = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const resp = await fetch(wsUrl, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Version: workstream.version, Paused: !workstream.paused }),
      });
      if (resp.ok) onWorkstreamChanged();
    } catch { /* ignore */ } finally {
      setBusy(false);
    }
  }, [busy, workstream.paused, workstream.version, wsUrl, onWorkstreamChanged]);

  const handleArchiveToggle = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const url = workstream.deleted ? `${wsUrl}/unarchive` : wsUrl;
      const method = workstream.deleted ? 'POST' : 'DELETE';
      const resp = await fetch(url, { method });
      if (resp.ok || resp.status === 204) onWorkstreamChanged();
    } catch { /* ignore */ } finally {
      setBusy(false);
    }
  }, [busy, workstream.deleted, wsUrl, onWorkstreamChanged]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <button
        type="button"
        title="Open planning session"
        aria-label="Open planning session"
        disabled={busy}
        onClick={() => navigate('/tasks/new')}
        style={iconBtnStyle}
      >
        <PlanIcon size={14} />
      </button>
      <button
        type="button"
        title={workstream.paused ? 'Start workstream' : 'Pause workstream'}
        aria-label={workstream.paused ? 'Start workstream' : 'Pause workstream'}
        disabled={busy}
        onClick={handleStartPause}
        style={iconBtnStyle}
      >
        {workstream.paused ? <PlayIcon size={14} /> : <PauseIcon size={14} />}
      </button>
      <button
        type="button"
        title={workstream.deleted ? 'Unarchive workstream' : 'Archive workstream'}
        aria-label={workstream.deleted ? 'Unarchive workstream' : 'Archive workstream'}
        disabled={busy}
        onClick={handleArchiveToggle}
        style={iconBtnStyle}
      >
        {workstream.deleted ? <UnarchiveIcon size={14} /> : <ArchiveIcon size={14} />}
      </button>
      <button
        type="button"
        title="Workstream details"
        aria-label="Workstream details"
        onClick={() => navigate(`/workstreams/${encodeURIComponent(workstream.workstreamID)}`)}
        style={iconBtnStyle}
      >
        <MoreIcon size={14} />
      </button>
    </div>
  );
}
